import React, {useState, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import './SavedSearchDataGridMoveFlyout.scss';
import {
  useMoveSavedSearches,
  useGetMySearchesTreeDetails,
} from '../../../../hooks/saved-search/useSavedSearch';
import {IMoveSavedSearchesPayload} from '../../../../api/types/savedsearch.types';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {getAllNestedIds} from '../../saved-search-utils';

// Types Definitions
// ----------------------------------

interface IndexDetailsindexData {
  indexId: number;
  indexName: string;
  indexStatus: string;
  indexSecurityGroupIds: number[];
}
interface SavedSearchDataGridMoveFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  savedSearchData: {
    savedSearchId: number;
    searchGroupID: number;
    searchGroupLabel: string;
    searchGroupType: string;
    searchGroupSecurityGroupId: number;
    toolTipRef: string;
    securityGroupId: number;
    savedSearches: any;
    indexData: IndexDetailsindexData;
  };
}
// ------------------

const SavedSearchDataGridMoveFlyout: React.FC<
  SavedSearchDataGridMoveFlyoutProps
> = ({isOpen = false, onClose, savedSearchData, onSave}) => {
  const [selectedTreeItem, setSelectedTreeItem] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [transformedTree, setTransformedTree] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const CompletedStatus = 'complete';
  const CompletedWEStatus = 'completedwe';
  const DefaultSecGroupId = '1';
  // ------------------

  // Fetch tree data from getMySearchesTreeDetails API
  const {
    data: mySearchesTreeData,
    isLoading,
    isError,
  } = useGetMySearchesTreeDetails();

  const [showLoader, setShowLoader] = useState<boolean>(false);

  useEffect(() => {
    const treeData = mySearchesTreeData?.searchesTreeData;

    if (Array.isArray(treeData) && treeData.length > 0) {
      const transformed = transformToTreeItems(treeData);
      setTransformedTree(transformed);

      if (transformed.length > 0 && !selectedTreeItem) {
        const firstNodeId = transformed[0].id;
        setSelectedTreeItem(firstNodeId);
      }
    }
  }, [mySearchesTreeData]);

  const isMySavedSearchFolder = (node: any): boolean => {
    const text = (node.text || node.label || '').toLowerCase().trim();
    const className = (
      node.a_attr?.class ||
      node.li_attr?.class ||
      ''
    ).toLowerCase();

    const isMySavedSearchByText =
      text === 'my saved search' ||
      text === 'mysavedsearch' ||
      text === 'my saved searches';

    const isMySavedSearchByClass = className.includes('mysavedsearch');

    const isRootNode = !node.parent || node.parent === '#';

    return isMySavedSearchByText || (isMySavedSearchByClass && isRootNode);
  };

  // Transform flat list to tree structure
  const transformToTreeItems = (
    flatList: any[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (!Array.isArray(flatList) || flatList.length === 0) {
      return [];
    }

    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();
    let mySavedSearchNodeId: string | null = null;

    for (const node of flatList) {
      if (!node.id) continue;

      if (isMySavedSearchFolder(node)) {
        mySavedSearchNodeId = node.id;
        continue; // Skip adding "My Saved Search" to the map
      }

      nodeMap.set(node.id, {
        id: node.id,
        label: node.text || node.id,
        showMoreOptions: false,
        children: [],
        fileType: 'folder',
        disabled: false,
        ...node,
      });
    }

    const tree: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
    const isRootNode = (parentId: string) =>
      !parentId ||
      parentId === '#' ||
      parentId === '' ||
      parentId === mySavedSearchNodeId;

    // Second pass: build hierarchy
    for (const node of flatList) {
      if (!node.id || node.id === mySavedSearchNodeId) continue;

      const current = nodeMap.get(node.id);
      if (!current) continue;

      if (isRootNode(node.parent)) {
        tree.push(current);
      } else {
        const parent = nodeMap.get(node.parent);
        parent?.children?.push(current);
      }
    }

    return tree;
  };

  const handleSelectedTreeItem = (items: string[] | null) => {
    if (items && items.length > 0) {
      setSelectedTreeItem(items[0]);
    }
  };
  // ------------------

  const {mutate: moveSavedSearch, isPending} = useMoveSavedSearches(
    () => {
      // Success handler
      setToastMessage(
        t('savedSearch.savedSearchDataGridMoveFlyout.moveSuccess')
      );
      setToastStatus([ToastType.Success]);
      setShowToast(true);
      // Call onSave to refresh the data but keep flyout open
      onSave();
    },
    (error: any) => {
      // Error handler
      setToastMessage(
        error?.detail ||
          t('savedSearch.savedSearchDataGridMoveFlyout.moveError')
      );
      setToastStatus([ToastType.Error]);
      setShowToast(true);
    }
  );
  // ------------------
  const handleSave = () => {
    if (mySearchesTreeData && savedSearchData) {
      setShowLoader(true);
      const selectedItem = (savedSearchData.savedSearches ?? []).find(
        (item: any) => item.id === String(selectedTreeItem)
      );
      const AlwaysAllowMoveIds = getAllNestedIds(savedSearchData.savedSearches);
      const {indexData} = savedSearchData;
      const targetIndexData = selectedItem?.indexData ?? [];
      const fromSecurityGroupId = indexData.indexSecurityGroupIds ?? [];
      const fromStandardIndexItem = indexData.indexId === 0;

      const fromCustomIndexwithNoSecurityGroupId =
        !fromStandardIndexItem && fromSecurityGroupId.length === 0;

      const indexStatus = indexData.indexStatus.toLowerCase();

      const toSecurityGroupId = [
        ...new Set(
          (targetIndexData ?? []).flatMap(
            (item: any) => item.indexSecurityGroupIds ?? []
          )
        ),
      ];

      const toDefaultSecGroup =
        selectedItem?.id === DefaultSecGroupId &&
        fromSecurityGroupId.includes(Number(DefaultSecGroupId));

      const canMove =
        AlwaysAllowMoveIds.includes(selectedItem?.id) ||
        toDefaultSecGroup ||
        fromStandardIndexItem ||
        (!fromCustomIndexwithNoSecurityGroupId &&
          (selectedItem?.indexData?.length ?? 0) > 0 &&
          indexStatus.includes(CompletedStatus) &&
          !indexStatus.includes(CompletedWEStatus) &&
          fromSecurityGroupId.length > 0 &&
          toSecurityGroupId.length > 0 &&
          fromSecurityGroupId.every((id) => toSecurityGroupId.includes(id)));

      if (canMove) {
        const payload: IMoveSavedSearchesPayload = {
          savedSearchId: savedSearchData.savedSearchId,
          searchGroupID: savedSearchData.searchGroupID,
          parentSearchGroupID: Number(selectedTreeItem),
          searchGroupLabel: savedSearchData.searchGroupLabel,
          searchGroupType: savedSearchData.searchGroupType,
          searchGroupSecurityGroupId:
            savedSearchData.searchGroupSecurityGroupId,
          toolTipRef: savedSearchData.toolTipRef,
        };

        moveSavedSearch(payload, {
          onSuccess: (data) => {
            // Update tree with the new structure from the API response
            setShowLoader(false);
            if (data?.treeStructureData) {
              const transformed = transformToTreeItems(data.treeStructureData);
              setTransformedTree(transformed);
            }
            onClose();
          },
          onError: () => {
            setShowLoader(false);
            setToastMessage(
              t('savedSearch.savedSearchDataGridMoveFlyout.saveError')
            );
            setToastStatus([ToastType.Error]);
            setShowToast(true);
          },
        });
      } else {
        setShowLoader(false);
        setToastMessage(
          t('savedSearch.savedSearchDataGridMoveFlyout.saveError')
        );
        setToastStatus([ToastType.Error]);
        setShowToast(true);
      }
    }
  };
  // ------------------

  return (
    <>
      <ArrDrawer
        isOpen={isOpen}
        onClose={onClose}
        title=""
        side="right"
        size="large"
        showButton={false}
        showHeader={false}
        className="saved-search-data-grid-move-flyout"
        ariaLabel="Saved Search Data Grid Move Flyout"
      >
        <Box className="saved-search-data-grid-move-flyout__header">
          <Box className="title-box">
            <Typography
              className=" typo-text headline3_size title"
              data-testid="flyout-title"
            >
              {t(`savedSearch.savedSearchDataGridMoveFlyout.flyoutTitle`)}
            </Typography>
          </Box>
          <Box className="action-buttons-box">
            <ArrButton
              variant="secondary"
              size="medium"
              onClick={() => {
                onClose();
              }}
              disabled={false}
              aria-label={t('common.cancel')}
              dataTestId="saved-search-data-grid-move-flyout-cancel-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <ArrButton
              disabled={!selectedTreeItem || isPending}
              variant="primary"
              size="medium"
              type="submit"
              aria-label={t('common.save')}
              dataTestId="saved-search-data-grid-save-button"
              onClick={handleSave}
            >
              {t('common.save')}
            </ArrButton>
          </Box>
        </Box>

        {/* Flyout Content */}
        <Box className="saved-search-data-grid-move-flyout__content">
          <Box className="saved-search-data-grid-move-flyout__tree-container">
            {isLoading && (
              <Typography
                className="typo-text body3_size"
                data-testid={`saved-search-data-grid-move-flyout-loading`}
              >
                {t('common.loading')}...
              </Typography>
            )}
            {isError && (
              <Typography
                className="typo-text body3_size"
                data-testid={`saved-search-data-grid-move-flyout-error-loading-data`}
              >
                {t('common.errorLoadingData')}
              </Typography>
            )}
            {!isLoading && !isError && transformedTree.length > 0 && (
              <ArrTreeView
                setSelectedItem={handleSelectedTreeItem}
                itemsList={transformedTree}
                selectedItem={selectedTreeItem}
                handleNoResultMessage={t('common.noData')}
                allowGlobalSelect={false}
                treeSelectedItems={[selectedTreeItem]}
                multipleSelection={false}
                isShowCountLabel={false}
                isShowCheckbox={false}
                showActions={false}
                isShowFileType={true}
                dataTestId="saved-search-move"
              />
            )}
            {!isLoading && !isError && transformedTree.length === 0 && (
              <Typography
                className="typo-text body3_size"
                data-testid={`saved-search-data-grid-move-flyout-no-data`}
              >
                {t('common.noData')}
              </Typography>
            )}
          </Box>
        </Box>
      </ArrDrawer>
      {/* Toast Notification */}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
      {showLoader && (
        <ArrLoadingSpinner show={showLoader} overlay={true} isOpen={true} />
      )}
    </>
  );
};

export default SavedSearchDataGridMoveFlyout;
